<template>
  <layout-public>
    <page-meta />
    <page-hero
      :crumbs="[
        { label: t('Home'), href: route('index') },
        { label: t('Services'), href: route('index') + '#services' },
        { label: t('Mobile application') }
      ]"
      :title="t('Mobile Application')"
      :lede="t('We build mobile applications for bookings, loyalty programmes, field teams or your own product, quoted as a single project from start to finish.')"
    />

    <service-block
      variant="plain"
      :kicker="t('Some products belong on the home screen.')"
      :title="t('Built like a product,\nnot published like a page.')"
      :lede="t('A website can take bookings or display a catalog, but cannot send push notifications or function offline. Here is what mobile applications are for.')"
      :aside-title="t('Start with what you have')"
      :aside-items="quoteNeeds"
    >
      <div class="action-row">
        <app-button href="#proposal" icon="arrow-down">
          {{ t('Request a proposal') }}
        </app-button>
      </div>
    </service-block>

    <section class="studio">
      <div class="container">
        <section-title
          data-aos="fade-up"
          :kicker="t('Previous work')"
          :title="t('Real products we designed and built')"
          :lede="t('Applications and portals we designed and built ourselves, now live and in daily use every single day by the real businesses that rely on them.')"
        />
        <div class="studio-cases">
          <article v-for="project in projects" :key="project.domain" class="studio-case" data-aos="fade-up">
            <figure class="studio-media" :class="{ 'is-app': project.mobile }">
              <div v-if="project.mobile" class="app-stage">
                <div class="app-device app-device--laptop">
                  <img class="app-shot" :src="project.image" :alt="t('{domain} on a desktop', { domain: project.domain })" />
                  <img class="app-frame" src="/images/home/hero/laptop-frame.png" alt="" aria-hidden="true" />
                </div>
                <div class="app-device app-device--phone">
                  <img class="app-shot" :src="project.mobile.image" :alt="t('{domain} on a phone', { domain: project.domain })" />
                  <img class="app-frame" src="/images/home/hero/phone-frame.png" alt="" aria-hidden="true" />
                </div>
              </div>
              <img v-else :src="project.image" :alt="project.domain" />
            </figure>
            <div class="studio-body">
              <img
                v-if="project.mobile"
                class="studio-app-icon"
                :src="project.mobile.icon"
                :alt="t('{domain} app icon', { domain: project.domain })"
              />
              <p class="studio-domain">{{ project.domain }}</p>
              <tag-list class="studio-badges" :tags="project.badges" />
              <p class="studio-copy">{{ project.copy }}</p>
              <app-button
                class="studio-cta"
                :href="project.url"
                target="_blank"
                rel="noopener"
                icon="arrow-up-right-from-square"
                icon-after
              >
                {{ t('Visit Website') }}
              </app-button>
            </div>
          </article>
        </div>
      </div>
    </section>

    <quote-band
      block-id="proposal"
      :title="t('Request a project proposal')"
      :lede="t('Every application is quoted as a project rather than a monthly subscription. Send your workflow and we come back with a detailed scope and fee.')"
      :cta="t('Get in touch')"
      :points="quotePoints"
    />

    <faq-list
      :kicker="t('Before you decide')"
      :title="t('Frequently asked questions')"
      :lede="t('How the entire build process works, how the app stores and your data are handled, and precisely what information we need from you beforehand.')"
      :items="faqs"
      accordion-id="mobile-faq"
    />
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.studio {
  background: $ground-tint;
}

/* The .ma-actions row is gone: the button owns its own hover and the gap comes
   from the global .action-row. */

/* One project per row, so each gets room to be read rather than scanned.
   The screenshot and the copy swap sides on every second row. */
.studio-cases {
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr);
}

.studio-case {
  background: $ground;
  border: 1px solid $line;
  border-radius: $r-lg;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  overflow: hidden;
  transition: border-color $dur $ease, box-shadow $dur $ease;

  /* Deliberately not the lift. A case card is a piece of work rather than a
     button, so the whole panel stays put and the motion happens inside the
     frame: the laptop pushes in, the phone rises off the stage, the blueprint
     ground drifts under both, and the halo widens behind them. The card itself
     only fades its edge in, which reads as the frame coming forward without
     the row moving. That is the one gesture on the site that shows the work
     instead of the card, so it stays this section's own.

     The copy column (.studio-body) is deliberately untouched by this gesture:
     the icon, the domain, the badges and the copy do not move, lift or
     recolour when the card is hovered. The visit button keeps its own hover,
     which is a link affordance rather than part of the card's motion. */
  &:hover,
  &:focus-within {
    border-color: $tint-primary;
    box-shadow: $shadow-band;

    .studio-media.is-app::before {
      background-position: 1.5rem 1.5rem;
    }

    .app-stage::before {
      transform: scale(1.1);
    }

    .app-device--laptop {
      transform: translateY(-6px) scale(1.02);
    }

    .app-device--phone {
      transform: translateY(-14px) scale(1.02);
    }

    /* The plain screenshot case, for a project with no phone shot: same idea,
       the shot settles a little deeper into its frame. */
    .studio-media > img {
      transform: scale(1.03);
    }
  }

  @media (min-width: 992px) {
    align-items: stretch;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);

    &:nth-child(even) {
      .studio-media {
        order: 2;
      }

      .studio-body {
        order: 1;
      }
    }
  }
}

.studio-media {
  margin: 0;
  position: relative;

  /* Portal and website cases: the browser screenshot fills the panel. */
  > img {
    display: block;
    object-fit: cover;
    object-position: top;
    transition: transform $dur-slow $ease;
    width: 100%;
  }

  /* Small screens: a fixed banner, so the card does not run tall before the copy. */
  @media (max-width: 991.98px) {
    > img {
      height: 13rem;
    }
  }

  /* Desktop: the figure stretches to the copy column and the shot fills it. */
  @media (min-width: 992px) {
    min-height: 20rem;

    > img {
      height: 100%;
      inset: 0;
      position: absolute;
    }
  }

  /* Application cases lead with the phone the buyer installs, so the panel
     becomes a stage rather than a screenshot: a pale blue ground the devices
     sit on, carrying the blueprint grid the services band uses. */
  &.is-app {
    align-items: center;
    background:
      radial-gradient(62% 78% at 50% 4%, $on-dark-body, transparent 64%),
      linear-gradient(135deg, $ground-accent, $line-blue);
    display: flex;
    justify-content: center;
    min-height: 20rem;
    padding: 1.75rem;

    @media (max-width: 991.98px) {
      min-height: 17rem;
      padding: 1.5rem;
    }

    /* The blueprint ground slides half a tile on hover, so the stage gains a
       sense of depth without the grid ever appearing to jump to a new
       position. Half a tile is the furthest the eye can travel while still
       reading the same grid. */
    &::before {
      background-image:
        linear-gradient(rgba($text-heading, .06) 1px, transparent 1px),
        linear-gradient(90deg, rgba($text-heading, .06) 1px, transparent 1px);
      background-size: 3rem 3rem;
      content: '';
      inset: 0;
      pointer-events: none;
      position: absolute;
      transition: background-position $dur-slow $ease;
    }
  }
}

.app-stage {
  max-width: 32rem;
  position: relative;
  width: 100%;

  /* Soft halo, so the devices sit in light rather than on flat blue. It widens
     on hover, which is what lifts the devices off the stage without moving
     them all the way out of it. */
  &::before {
    background: radial-gradient(closest-side, $on-dark-body, transparent 74%);
    content: '';
    inset: -10% -22%;
    position: absolute;
    transition: transform $dur-slow $ease;
  }
}

/* Two stacked layers per device: the screenshot sits inside the transparent
   screen of the frame PNG, and the frame is painted over it. The insets below
   are the glass rect measured off each frame asset, as a percentage of the
   asset, so the shot stays inside the glass at any width.

   Measured, not estimated (the alpha hole in each PNG):
     phone   220 x 439, glass x 14..205  y 13..427
     laptop  890 x 509, glass x 100..788 y 31..463
   Re-measure if either frame asset is ever replaced. Both frames are the ones
   the hero already ships. */
.app-device {
  position: relative;
  /* The device group moves as one: the frame and the screenshot inside it share
     a single transform, so the shot can never slide out of the glass. */
  transition: transform $dur-slow $ease;

  .app-shot {
    position: absolute;
    z-index: 1;
  }

  .app-frame {
    display: block;
    filter: drop-shadow(0 1rem 1.5rem rgba($ink, .2));
    position: relative;
    width: 100%;
    z-index: 2;
  }
}

.app-device--laptop {
  width: 84%;

  .app-shot {
    height: 85.07%;
    left: 11.24%;
    object-fit: cover;
    object-position: top;
    top: 6.09%;
    width: 77.42%;
  }
}

.app-device--phone {
  bottom: 0;
  position: absolute;
  right: 0;
  width: 22%;
  z-index: 3;

  /* 2.96% / 94.53%, not 6.38% / 91.34%. The old pair anchored the bottom edge
     correctly but started the shot 15px below the top of the glass, which left
     a strip of the stage showing through the top of the screen. */
  .app-shot {
    height: 94.53%;
    left: 6.36%;
    object-fit: cover;
    object-position: top;
    top: 2.96%;
    width: 87.27%;
  }
}

/* The store icon opens the copy column, above the domain heading, the way an
   app listing leads with its tile. The ring is thick but light grey, so it
   reads as a soft edge against the white card rather than a hard outline. */
.studio-app-icon {
  align-self: flex-start;
  border: 4px solid rgba($text-heading, .09);
  border-radius: $r-lg;
  box-shadow: $shadow-lift;
  height: 4rem;
  margin-bottom: 1.15rem;
  width: 4rem;
}

.studio-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.75rem 1.6rem;

  @media (min-width: 992px) {
    padding: 2.5rem 2.5rem 2.5rem 2.25rem;
  }
}

/* The project domain, a card label rather than a heading. It used to be an h2
   inside a section whose own head is already the h2, so every case claimed the
   same outline level as the section that contains it. Same demotion the
   portfolio's domain labels got. The label is not a link: the card already
   carries one call to action at its foot, and two links to the same url in one
   card gave the same destination two different names. */
.studio-domain {
  color: $ground-dark;
  font-family: $headings-font-family;
  font-size: 1.35rem;
  font-weight: 500;
  margin-bottom: 0.85rem;
}

/* Category pills, using the same tags the landing page portfolio shows. */
/* Only the card's own spacing is decided here; the chip is TagList's. */
.studio-badges {
  margin-bottom: 1.1rem;
}

.studio-copy {
  color: $text-body;
  font-size: 1rem;
  line-height: 1.65;
  margin-bottom: 1.5rem;
}

/* The visit button. Bootstrap's own .btn-primary, which is what every other
   action on the site uses (every service intro, the quote band and the CTA
   form), with the icon first and `me-2` like all of them. The class used to be
   .btn-outline-primary, which appears nowhere else on the site, and it carried
   no alignment: .studio-body is a flex column, so the button stretched to the
   full column width and read as an empty box rather than a button.

   The only thing declared here is the alignment, because inline size is the
   one thing the global class cannot know about its context. */
.studio-cta {
  align-self: flex-start;
}
</style>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  faArrowDown,
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faCamera,
  faClipboardCheck,
  faLayerGroup,
  faMobileScreenButton,
  faUserShield,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import PageMeta from '@/Components/PageMeta.vue';
import AppButton from '@/Components/AppButton.vue';
import FaqList from '@/Components/FaqList.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import PageHero from '@/Components/PageHero.vue';
import QuoteBand from '@/Components/QuoteBand.vue';
import ServiceBlock from '@/Components/ServiceBlock.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import TagList from '@/Components/TagList.vue';

FontAwesomeLibrary.add(
  faArrowDown,
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faCamera,
  faClipboardCheck,
  faLayerGroup,
  faMobileScreenButton,
  faUserShield,
  faUsers
);

export default {
  components: {
    AppButton,
    FaqList,
    PageMeta,
    LayoutPublic,
    PageHero,
    QuoteBand,
    SectionTitle,
    ServiceBlock,
    TagList
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Mobile Application': 'Mobile Application',
          'A Figma file or a rough list is enough. We turn it into a scope you can approve.': 'A Figma file or a rough list is enough. We turn it into a scope you can approve.',
          'Some products belong on the home screen.': 'Some products belong on the home screen.',
          'Built like a product,\nnot published like a page.': 'Built like a product,\nnot published like a **page**.',
          'After launch': 'After launch',
          'A website can take bookings or display a catalog, but cannot send push notifications or function offline. Here is what mobile applications are for.': 'A website can take bookings or display a catalog, but cannot send push notifications or function offline. Here is what mobile applications are for.',
          'We build mobile applications for bookings, loyalty programmes, field teams or your own product, quoted as a single project from start to finish.': 'We build mobile applications for bookings, loyalty programmes, field teams or your own product, quoted as a single project from start to finish.',
          'Customers, staff, or both. We map the real journey first, so the screens fit the work.': 'Customers, staff, or both. We map the real journey first, so the screens fit the work.',
          'Features worth building': 'Features worth building',
          'Applications and portals we designed and built ourselves, now live and in daily use every single day by the real businesses that rely on them.': 'Applications and portals we designed and built ourselves, now live and in daily use every single day by the real businesses that rely on them.',
          'How the entire build process works, how the app stores and your data are handled, and precisely what information we need from you beforehand.': 'How the entire build process works, how the app stores and your data are handled, and precisely what information we need from you beforehand.',
          'How long does an application take?': 'How long does an application take?',
          'Is a mobile application inside the web design plans?': 'Is a mobile application inside the web design plans?',
          'Login, payments, camera, maps or offline. We keep what earns its place in the build.': 'Login, payments, camera, maps or offline. We keep what earns its place in the build.',
          'Real products we designed and built': '**Real products** we designed and built',
          'Native apps or one build for both phones?': 'Native apps or one build for both phones?',
          'New OS versions, store rule changes and extra screens are new work. We fix anything we broke, at no charge.': 'New OS versions, store rule changes and extra screens are new work. We fix anything we broke, at no charge.',
          'Start on one store and prove it. Add the second when the first pays for itself.': 'Start on one store and prove it. Add the second when the first pays for itself.',
          'We designed and built the YeasyTrip website, with a shoppable catalog, so a traveller can read an itinerary, compare stays and places to eat, and book the trip on the same site.': 'We designed and built the YeasyTrip website, with a shoppable catalog, so a traveller can read an itinerary, compare stays and places to eat, and book the trip on the same site.',
          'Previous work': 'Previous work',
          'We prepare the build, the store screenshots and the listing copy for both stores, all of it ready to submit.': 'We prepare the build, the store screenshots and the listing copy for both stores, all of it ready to submit.',
          'A file or just an idea': 'A file or just an idea',
          'Every application is quoted as a project rather than a monthly subscription. Send your workflow and we come back with a detailed scope and fee.': 'Every application is quoted as a project rather than a monthly subscription. Send your workflow and we come back with a detailed scope and fee.',
          'Request a proposal': 'Request a proposal',
          '{domain} app icon': '{domain} app icon',
          'What do you need from me to quote?': 'What do you need from me to quote?',
          'What happens after it is live?': 'What happens after it is live?',
          'Start with what you have': 'Start with what you have',
          'What we prepare': 'What we prepare',
          'Where does the application store its data?': 'Where does the application store its data?',
          'Ship one store, then two': 'Ship one store, then two',
          'Who puts it on the App Store and Play Store?': 'Who puts it on the App Store and Play Store?',
          'Built around your users': 'Built around your users',
          'You need developer accounts held in your own company name, because Apple and Google review every single release.': 'You need developer accounts held in your own company name, because Apple and Google review every single release.',
          'Your accounts': 'Your accounts',
          'Zerva is an online booking system for salons, clinics and studios. Customers book, the team shares one calendar, and WhatsApp updates sit in the same workflow.': 'Zerva is an online booking system for salons, clinics and studios. Customers book, the team shares one calendar, and WhatsApp updates sit in the same workflow.',
          'iPhone, Android, or both?': 'iPhone, Android, or both?',
          'faq-after': 'New OS versions, store rule changes and extra screens are new work. Anything we broke, we fix at no charge. Store fees and developer accounts stay in your company name.',
          'faq-data': 'On a server we quote with the job, or on a system you already run. Login, orders and staff tools need that backend. The application on the phone is only half of the product.',
          'faq-inside': 'No. Those plans are for a website. An application is a separate project with its own project proposal, stores, and a quote.',
          'faq-native': 'We pick from the project proposal. A shared codebase can ship to both stores. A native build is for work that needs deep device features.',
          'faq-quote': 'Who the users are, which stores, the main screens, and whether it needs login, payments, camera, maps, push notifications or offline use.',
          'faq-stores': 'Say who the users are before we quote. One store is a smaller first release. Both stores means two listings, two reviews, and usually more budget.',
          'faq-submit': 'You need developer accounts in your company name. We prepare the build, screenshots and listing copy. Apple and Google still review each release.',
          'faq-time': 'The date is in the project proposal. It depends on screens, payments, offline use, store accounts, and who hosts the data.'
        },
        'zh-MY': {
          'Mobile Application': '移动应用',
          'A Figma file or a rough list is enough. We turn it into a scope you can approve.': '一份 Figma 文件或粗略清单就足够。我们把它变成您可确认的范围。',
          'Some products belong on the home screen.': '有些产品，就该待在手机主画面。',
          'After launch': '上线之后',
          'Built like a product,\nnot published like a page.': '像产品一样打造，\n而不是像**网页**一样发布。',
          'We build mobile applications for bookings, loyalty programmes, field teams or your own product, quoted as a single project from start to finish.': '我们为预约、会员回馈、外勤团队，或您自己的产品开发手机应用，以单一项目的方式报价，不绑月费，也不抽成，范围与费用一开始就说得清楚。',
          'Customers, staff, or both. We map the real journey first, so the screens fit the work.': '客户、员工，或两者皆是。我们先梳理真实流程，让画面贴合实际工作。',
          'Features worth building': '只做值得的功能',
          'Applications and portals we designed and built ourselves, now live and in daily use every single day by the real businesses that rely on them.': '以下是我们亲手设计、开发并已经上线的应用与门户，每天都有真实的企业在使用，靠它们处理日常营运，绝对不是摆着好看的示范作品，而是天天在跑的。',
          'How the entire build process works, how the app stores and your data are handled, and precisely what information we need from you beforehand.': '制作流程如何进行、应用商店与您的数据如何处理，以及开工之前我们需要您提供什么，都清楚写在这一页，您可以先仔细看一遍再决定要不要做。',
          'How long does an application take?': '应用程序需要多久？',
          'Is a mobile application inside the web design plans?': '网页设计配套包含移动应用吗？',
          'Login, payments, camera, maps or offline. We keep what earns its place in the build.': '登录、付款、相机、地图或离线。我们只保留在项目里真正有价值的功能。',
          'Real products we designed and built': '我们设计并打造的**真实产品**',
          'Native apps or one build for both phones?': '原生应用，还是一套程序对应两种手机？',
          'New OS versions, store rule changes and extra screens are new work. We fix anything we broke, at no charge.': '新系统版本、商店规则变更与额外画面属于新工作。我们造成的问题，我们免费修复。',
          'Start on one store and prove it. Add the second when the first pays for itself.': '先在一个商店上线验证，等它开始回本，再上第二个。',
          'We designed and built the YeasyTrip website, with a shoppable catalog, so a traveller can read an itinerary, compare stays and places to eat, and book the trip on the same site.': '我们为 YeasyTrip 设计并搭建了带可购买目录的网站，让旅客可以先读行程、比较住宿与餐厅，然后在同一个网站上完成预订。',
          'Previous work': '过往作品',
          'We prepare the build, the store screenshots and the listing copy for both stores, all of it ready to submit.': '我们准备好构建、两个商店的截图与上架文案，随时可以提交给应用商店审核。',
          'A file or just an idea': '有文件，或只有想法',
          'Every application is quoted as a project rather than a monthly subscription. Send your workflow and we come back with a detailed scope and fee.': '每一个应用都以项目方式报价，而不是月费配套。把您的工作流程发给我们，我们会回复一份写明范围与费用的建议书，内容清楚，绝不含糊，清楚透明。',
          'Request a proposal': '索取项目提案',
          '{domain} app icon': '{domain} 应用图标',
          'A website can take bookings or display a catalog, but cannot send push notifications or function offline. Here is what mobile applications are for.': '网站可以接受预约或展示目录，但无法推送通知，也不能离线使用。这就是手机应用存在的理由，也是它真正值得您认真考虑、投资与评估的好地方。',
          'What do you need from me to quote?': '报价需要我提供什么？',
          'What happens after it is live?': '上线之后呢？',
          'Start with what you have': '从您现有的开始',
          'What we prepare': '我们准备什么',
          'Where does the application store its data?': '应用程序的数据存在哪里？',
          'Ship one store, then two': '先上一个商店，再上两个',
          'Who puts it on the App Store and Play Store?': '谁负责上架 App Store 与 Play 商店？',
          'Built around your users': '围绕您的使用者打造',
          'You need developer accounts held in your own company name, because Apple and Google review every single release.': '您需要以公司名义持有开发者账户，因为苹果与谷歌会审核每一次发布的内容。',
          'Your accounts': '您的账户',
          'Zerva is an online booking system for salons, clinics and studios. Customers book, the team shares one calendar, and WhatsApp updates sit in the same workflow.': 'Zerva 是面向沙龙、诊所与工作室的在线预约系统。客户预约、团队共用同一日历，WhatsApp 更新也在同一流程中。',
          'iPhone, Android, or both?': 'iPhone、Android，还是两者？',
          'faq-after': '新系统版本、商店规则变更与额外画面属于新工作。由我们造成的问题，我们免费修复。商店费用与开发者账户保持在您的公司名下。',
          'faq-data': '在我们随项目报价的服务器上，或在您现有的系统上。登录、订单与员工工具需要该后端。手机上的应用程序只是产品的一半。',
          'faq-inside': '不含。那些配套是为网站而设。应用程序是独立项目，有自己的项目提案、商店与报价。',
          'faq-native': '我们按项目提案选择。共用代码库可以发到两个商店。原生构建用于需要深入设备功能的工作。',
          'faq-quote': '使用者是谁、上架哪些商店、主要画面，以及是否需要登录、付款、相机、地图、推送通知或离线使用。',
          'faq-stores': '报价前请说明使用者是谁。只上架一个商店是较小的首发。两个商店代表两个上架、两次审核，预算通常也更高。',
          'faq-submit': '您需要以公司名义开设开发者账户。我们准备构建、截图与上架文案。苹果与谷歌仍会审核每一次发布。',
          'faq-time': '日期写在项目提案里。取决于画面、付款、离线使用、商店账户，以及数据由谁托管。'
        },
        'ms-MY': {
          'Mobile Application': 'Aplikasi Mudah Alih',
          'A Figma file or a rough list is enough. We turn it into a scope you can approve.': 'Fail Figma atau senarai kasar sudah cukup. Kami jadikannya skop yang boleh anda luluskan.',
          'Some products belong on the home screen.': 'Sesetengah produk memang patut berada di skrin utama.',
          'After launch': 'Selepas dilancarkan',
          'Built like a product,\nnot published like a page.': 'Dibina seperti produk,\nbukan diterbitkan seperti **halaman**.',
          'We build mobile applications for bookings, loyalty programmes, field teams or your own product, quoted as a single project from start to finish.': 'Kami membina aplikasi mudah alih untuk tempahan, program kesetiaan, pasukan lapangan atau produk anda sendiri, dengan harga satu projek yang lengkap.',
          'Customers, staff, or both. We map the real journey first, so the screens fit the work.': 'Pelanggan, kakitangan, atau kedua-duanya. Kami petakan perjalanan sebenar dahulu, supaya skrin sepadan dengan kerja.',
          'Features worth building': 'Ciri yang berbaloi dibina',
          'Applications and portals we designed and built ourselves, now live and in daily use every single day by the real businesses that rely on them.': 'Aplikasi dan portal yang kami sendiri reka dan bina, kini aktif dan benar-benar digunakan setiap hari oleh perniagaan yang mengamanahkannya.',
          'How the entire build process works, how the app stores and your data are handled, and precisely what information we need from you beforehand.': 'Bagaimana proses pembinaan berjalan, bagaimana kedai aplikasi dan data anda diuruskan, dan apa yang kami perlukan daripada anda sebelum kerja bermula.',
          'How long does an application take?': 'Berapa lama aplikasi mengambil masa?',
          'Is a mobile application inside the web design plans?': 'Adakah aplikasi mudah alih dalam pelan reka bentuk web?',
          'Login, payments, camera, maps or offline. We keep what earns its place in the build.': 'Log masuk, pembayaran, kamera, peta atau luar talian. Kami kekalkan yang berbaloi dalam binaan.',
          'Real products we designed and built': '**Produk sebenar** yang kami reka dan bina',
          'Native apps or one build for both phones?': 'Aplikasi natif atau satu binaan untuk kedua-dua telefon?',
          'New OS versions, store rule changes and extra screens are new work. We fix anything we broke, at no charge.': 'Versi OS baharu, peraturan kedai yang berubah dan skrin tambahan ialah kerja baharu. Apa yang kami rosakkan, kami baiki.',
          'Start on one store and prove it. Add the second when the first pays for itself.': 'Mula dengan satu kedai dan buktikan. Tambah yang kedua apabila ia mula membayar balik.',
          'We designed and built the YeasyTrip website, with a shoppable catalog, so a traveller can read an itinerary, compare stays and places to eat, and book the trip on the same site.': 'Kami mereka bentuk dan membina laman web YeasyTrip, dengan katalog yang boleh dibeli, supaya pengembara boleh membaca itinerari, membandingkan penginapan dan tempat makan, kemudian menempah perjalanan di laman yang sama.',
          'Previous work': 'Kerja terdahulu',
          'We prepare the build, the store screenshots and the listing copy for both stores, all of it ready to submit.': 'Kami sediakan binaan, tangkapan skrin kedai dan salinan senarai untuk kedua-dua kedai, semuanya sedia untuk dihantar.',
          'A file or just an idea': 'Fail, atau sekadar idea',
          'Every application is quoted as a project rather than a monthly subscription. Send your workflow and we come back with a detailed scope and fee.': 'Setiap aplikasi dikuotakan mengikut projek, bukan sebagai pelan bulanan. Hantar aliran kerja anda dan kami balas dengan skop bertulis serta bayaran.',
          'Request a proposal': 'Minta cadangan projek',
          '{domain} app icon': 'Ikon aplikasi {domain}',
          'A website can take bookings or display a catalog, but cannot send push notifications or function offline. Here is what mobile applications are for.': 'Laman web boleh menerima tempahan atau tunjuk katalog, tetapi tidak boleh menghantar notifikasi atau berfungsi luar talian. Inilah gunanya aplikasi.',
          'What do you need from me to quote?': 'Apa yang anda perlukan daripada saya untuk sebut harga?',
          'What happens after it is live?': 'Apa jadi selepas ia dalam talian?',
          'Start with what you have': 'Mula dengan apa yang anda ada',
          'What we prepare': 'Apa yang kami sediakan',
          'Where does the application store its data?': 'Di mana aplikasi menyimpan datanya?',
          'Ship one store, then two': 'Satu kedai dahulu, kemudian dua',
          'Who puts it on the App Store and Play Store?': 'Siapa meletakkannya di App Store dan Play Store?',
          'Built around your users': 'Dibina mengelilingi pengguna anda',
          'You need developer accounts held in your own company name, because Apple and Google review every single release.': 'Anda perlukan akaun pembangun atas nama syarikat anda sendiri, kerana Apple dan Google masih menyemak setiap keluaran.',
          'Your accounts': 'Akaun anda',
          'Zerva is an online booking system for salons, clinics and studios. Customers book, the team shares one calendar, and WhatsApp updates sit in the same workflow.': 'Zerva ialah sistem tempahan dalam talian untuk salon, klinik dan studio. Pelanggan menempah, pasukan berkongsi satu kalendar, dan kemas kini WhatsApp berada dalam aliran kerja yang sama.',
          'iPhone, Android, or both?': 'iPhone, Android, atau kedua-duanya?',
          'faq-after': 'Versi OS baharu, perubahan peraturan kedai dan skrin tambahan ialah kerja baharu. Apa sahaja yang kami rosakkan, kami baiki tanpa caj. Yuran kedai dan akaun pembangun kekal atas nama syarikat anda.',
          'faq-data': 'Pada pelayan yang kami sebut harga bersama kerja itu, atau pada sistem yang sudah anda jalankan. Log masuk, pesanan dan alatan kakitangan perlukan backend itu. Aplikasi pada telefon hanyalah separuh daripada produk.',
          'faq-inside': 'Tidak. Pelan itu adalah untuk laman web. Aplikasi ialah projek berasingan dengan cadangan projek, kedai dan sebut harga sendiri.',
          'faq-native': 'Kami pilih daripada cadangan projek. Pangkalan kod kongsi boleh dihantar ke kedua-dua kedai. Binaan natif adalah untuk kerja yang perlukan ciri peranti yang dalam.',
          'faq-quote': 'Siapa pengguna, kedai mana, skrin utama, dan sama ada ia perlukan log masuk, pembayaran, kamera, peta, pemberitahuan tolak atau penggunaan luar talian.',
          'faq-stores': 'Nyatakan siapa pengguna sebelum kami sebut harga. Satu kedai ialah keluaran pertama yang lebih kecil. Kedua-dua kedai bermakna dua senarai, dua semakan, dan biasanya lebih bajet.',
          'faq-submit': 'Anda perlukan akaun pembangun atas nama syarikat anda. Kami sediakan binaan, tangkapan skrin dan salinan senarai. Apple dan Google masih menyemak setiap keluaran.',
          'faq-time': 'Tarikh ada dalam cadangan projek. Ia bergantung pada skrin, pembayaran, penggunaan luar talian, akaun kedai, dan siapa yang hos data.'
        }
      }
    });

    const faqs = computed(() => [
      { q: t('Is a mobile application inside the web design plans?'), a: t('faq-inside') },
      { q: t('iPhone, Android, or both?'), a: t('faq-stores') },
      { q: t('Native apps or one build for both phones?'), a: t('faq-native') },
      { q: t('What do you need from me to quote?'), a: t('faq-quote') },
      { q: t('How long does an application take?'), a: t('faq-time') },
      { q: t('Who puts it on the App Store and Play Store?'), a: t('faq-submit') },
      { q: t('Where does the application store its data?'), a: t('faq-data') },
      { q: t('What happens after it is live?'), a: t('faq-after') }
    ]);

    const quoteNeeds = computed(() => [
      { icon: 'mobile-screen-button', title: t('Ship one store, then two'), body: t('Start on one store and prove it. Add the second when the first pays for itself.') },
      { icon: 'users', title: t('Built around your users'), body: t('Customers, staff, or both. We map the real journey first, so the screens fit the work.') },
      { icon: 'layer-group', title: t('A file or just an idea'), body: t('A Figma file or a rough list is enough. We turn it into a scope you can approve.') },
      { icon: 'camera', title: t('Features worth building'), body: t('Login, payments, camera, maps or offline. We keep what earns its place in the build.') }
    ]);

    const quotePoints = computed(() => [
      { icon: 'user-shield', title: t('Your accounts'), body: t('You need developer accounts held in your own company name, because Apple and Google review every single release.') },
      { icon: 'clipboard-check', title: t('What we prepare'), body: t('We prepare the build, the store screenshots and the listing copy for both stores, all of it ready to submit.') },
      { icon: 'arrows-rotate', title: t('After launch'), body: t('New OS versions, store rule changes and extra screens are new work. We fix anything we broke, at no charge.') }
    ]);

    /* The work section is data, so a new project is one entry here. Badges match
       the category tags the landing page portfolio shows for the same project. */
    const projects = computed(() => [
      {
        domain: 'zerva.my',
        url: 'https://zerva.my/',
        image: '/images/portfolios/screenshots/desktop/zerva/cover.webp',
        /* Has a phone screenshot, so the card shows the device instead. */
        mobile: {
          image: '/images/portfolios/screenshots/mobile/zerva.webp',
          icon: '/images/portfolios/screenshots/mobile/zerva-icon.png'
        },
        badges: [t('Business'), t('Portal')],
        copy: t('Zerva is an online booking system for salons, clinics and studios. Customers book, the team shares one calendar, and WhatsApp updates sit in the same workflow.')
      },
      {
        domain: 'yeasytrip.com',
        url: 'https://yeasytrip.com/',
        image: '/images/portfolios/screenshots/desktop/yeasytrip/cover.webp',
        /* Has a phone screenshot, so the card shows the device instead. */
        mobile: {
          image: '/images/portfolios/screenshots/mobile/yeasytrip.webp',
          icon: '/images/portfolios/screenshots/mobile/yeasytrip-icon.png'
        },
        badges: [t('Business'), t('E-Commerce')],
        copy: t('We designed and built the YeasyTrip website, with a shoppable catalog, so a traveller can read an itinerary, compare stays and places to eat, and book the trip on the same site.')
      }
    ]    );

    return { t, faqs, projects, quoteNeeds, quotePoints };
  }
};
</script>
